import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';

import Strings from '../../../Strings';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import { showStatus } from '../../../utils';
import { notificationActions } from '../../../components/Notification/redux/actions';
import { PageHeader } from '../../../components/PageHeader';
import { TextInput } from '../../../components/PageHeader/TextInput';
import { CONFIGURE_SMARTMETER_SKU_RESULT, GET_CATALOG_RESULT, GET_SMARTMETER_SKUS_RESULT, SET_GLOBAL_ORDERABLE_ERROR, shippingActions } from '../../Shipping/actions';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition, ManufactuterFilter } from '../../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { getRelevantFilters } from '../../../components/AdvancedFilters/helpers';
import { ActionDropdownOnOffMenu, ActionWidget } from '../../../components/ActionWidget';
import { isDevelopment } from '../../../utils/environment';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { isSuperUser } from '../../../utils/userRoles';
import SimpleModal from '../../../containers/Modal/SimpleModal';
import { MultiSelectField, RadioButton, SelectField } from '../../../containers/Form';
import { SelectedOptions, SelectedOptionsType } from '../../../components/Select/Multiselect';
import { groupSmartmeterSkuOptions, modelTypesFromDevices } from '../../Shipping/smartmeterSkuGrouping';

function SmartmeterConfigModal({ device, onCancel, onSubmit, smartmeterSkus, modelTypes }) {
  const [config, setConfig] = React.useState({ smartmeter_sku: device.smartmeter_sku, variants: device.smartmeter_variants?.map(v => v.sku) });
  const [selectedOption, setSelectedOption] = React.useState(device.smartmeter_sku ? 'sku' : 'variants');

  // Accessory/no-device SKUs are excluded: the backend rejects them as device
  // mappings (smartmeter_sku_no_device); accessory orders go through the
  // shipper's per-order SKU override instead.
  const groupedSkuOptions = React.useMemo(
    () => groupSmartmeterSkuOptions(smartmeterSkus, device.device_model, device.device_type, modelTypes),
    [smartmeterSkus, device.device_model, device.device_type, modelTypes],
  );

  return (
    <SimpleModal
      name="smartmeter-config-modal"
      title={Strings.formatString(Strings.shipping.smartmeterConfigureSkuFor, device.device_model)}
      onCancel={onCancel}
      onSubmit={() => onSubmit({
        device_uuid: device.device_uuid,
        smartmeter_sku: selectedOption === 'sku' ? config.smartmeter_sku : undefined,
        variants: selectedOption === 'variants' ? config.variants : undefined
      })}
      content={(
        <>
          <RadioButton
            type="radio"
            name="sku"
            id="sku"
            value="sku"
            label={Strings.shipping.smartmeterSku}
            containerClass="for-radio"
            checked={selectedOption === 'sku'}
            onChange={() => setSelectedOption('sku')}
          />
          {selectedOption === 'sku' && (
            <SelectField
              id="smartmeter_sku"
              name="smartmeter_sku"
              label={Strings.shipping.smartmeterSku}
              value={config.smartmeter_sku}
              onChange={option => setConfig({ ...config, smartmeter_sku: option.value })}
              data={groupedSkuOptions}
            />
          )}
          <RadioButton
            type="radio"
            name="variants"
            id="variants"
            value="variants"
            label={Strings.shipping.variants}
            containerClass="for-radio"
            checked={selectedOption === 'variants'}
            onChange={() => setSelectedOption('variants')}
          />
          {selectedOption === 'variants' && (
            <>
              <MultiSelectField
                name="variants"
                label={Strings.shipping.variants}
                value={config.variants}
                onChange={options => setConfig({ ...config, variants: options.map(o => o.value) })}
                data={groupedSkuOptions}
                closeMenuOnSelect={false}
                checkboxFirst
              />
              {config.variants?.length > 0 && (
                <SelectedOptions
                  id="variants"
                  items={config.variants.map(v => {
                    const sku = smartmeterSkus.find(s => s.sku === v);
                    return { value: v, label: sku?.description || v };
                  })}
                  label={Strings.shipping.variants}
                  options={smartmeterSkus.map(sku => ({ value: sku.sku, label: sku.description }))}
                  className="grid-full-width"
                  type={SelectedOptionsType.cloud}
                  withClearButton={false}
                  onRemove={option => setConfig({ ...config, variants: config.variants.filter(v => v !== option.value) })}
                />
              )}
            </>
          )}
        </>
      )}
    />
  );
};

function DeviceCatalog(props) {
  const { getCatalog, setGlobalOrderable, getSmartmeterSkus, smartmeterBootstrap, configureSmartmeterSku, showNotification, filters } = props;
  const [data, setData] = React.useState([]);
  const [smartmeterSkus, setSmartmeterSkus] = React.useState([]);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedDevice, setSelectedDevice] = React.useState(null);
  const [showSmartmeterConfigModal, setShowSmartmeterConfigModal] = React.useState(false);
  const [devicesToDisable, setDevicesToDisable] = React.useState(null);

  const tableRef = React.useRef(null);

  const load = React.useCallback(() => {
    getCatalog({include_disabled: true}).then(resp => {
      if (resp?.type === GET_CATALOG_RESULT) {
        setData(resp?.response?.entries || []);
      }
    });
    tableRef?.current?.resetSelectedRows();

    getSmartmeterSkus().then(resp => {
      if (resp?.type === GET_SMARTMETER_SKUS_RESULT) {
        setSmartmeterSkus(resp?.response?.skus || []);
      }
    });
  }, [getCatalog]);

  React.useEffect(() => {
    load();
  }, [load]);

  const columns = React.useMemo(() => [
    columnHelper.accessor('device_uuid', {
      header: Strings.id,
      size: 90,
    }),
    columnHelper.accessor('device_manufacturer', {
      header: Strings.manufacturer,
      size: 60,
    }),
    columnHelper.accessor('device_model', {
      header: Strings.deviceModel,
      accessorFn: row => `${row.device_model} ${row.smartmeter_sku ? `(${row.smartmeter_sku})` : ''}`.trim(),
      size: 40,
    }),
    columnHelper.accessor('device_type', {
      header: Strings.deviceType,
      size: 40,
    }),
    columnHelper.accessor('is_globally_orderable', {
      header: "Globally Orderable",
      size: 20,
      cell: info => showStatus(!!info.getValue()),
    }),
  ], []);
  
  const dataForTable = React.useMemo(() => {
    const propertiesToSearch = ['device_uuid', 'device_manufacturer', 'device_model', 'device_type'];
    let ret = data.filter(task => propertiesToSearch.some(prop => (task[prop] || '').toLowerCase().includes(searchQuery.trim().toLowerCase())));

    const appliedFilters = getRelevantFilters(filters);
      const filtersToCheck = [
        { filterGrp: 'manufacturer', key: 'device_manufacturer' },
        { filterGrp: 'DeviceType', key: 'device_type' }
      ];

      const hasFilters = filtersToCheck.some(f => appliedFilters?.some(af => af.group === f.filterGrp));
      if (hasFilters) {
        filtersToCheck.forEach(filterGroup => {
          const filteredByGroup = appliedFilters?.filter(f => f.group === filterGroup.filterGrp);
          if (filteredByGroup?.length) {
            ret = ret.filter(p => filteredByGroup.some(f => f.value === p[filterGroup.key]));
          }
        });
      }
    return ret;
  }, [data, searchQuery, filters]);

  const buttons = React.useMemo(() => [
    {
      icon: 'edit',
      onClick: (_, device) => { setSelectedDevice(device); setShowSmartmeterConfigModal(true); },
      text: "Configure Smartmeter SKU",
      visible: (_, device) => device.device_manufacturer === 'smartmeter' && isSuperUser(),
    },
  ], []);

  const setDevicesOrderable = React.useCallback(async (devices, orderable) => {
    let fail = false;
    await Promise.all(devices.map(async d => {
      const r = await setGlobalOrderable({ device_uuid: d.device_uuid, is_orderable: orderable });
      if (r.type === SET_GLOBAL_ORDERABLE_ERROR) {
        showNotification(r.response?.data?.standard_error?.message, 5000, true);
        fail = true;
      }
    }));
    if (!fail) {
      showNotification('Devices updated');
      load();
    }
  }, [setGlobalOrderable, showNotification, load]);

  // Withdrawing a device leaves it in every mapping set that references it, so
  // the consequence has to be stated before it is applied.
  const onIsOrderableAction = React.useCallback((devices, value) => {
    if (value === 1) {
      setDevicesOrderable(devices, true);
      return;
    }
    setDevicesToDisable(devices);
  }, [setDevicesOrderable]);

  const onConfigureSmartmeterSku = React.useCallback(function submit(device, allowModelMismatch = false) {
    const reqData = {
      device_uuid: device.device_uuid,
      smartmeter_sku: device.smartmeter_sku,
      variants: device.variants?.map(v => ({ sku: v, label: smartmeterSkus.find(s => s.sku === v)?.description || v })) || undefined,
      allow_model_mismatch: allowModelMismatch || undefined,
    };
    configureSmartmeterSku(reqData).then(resp => {
      if (resp?.type !== CONFIGURE_SMARTMETER_SKU_RESULT) {
        const errorBody = resp?.response?.data;
        // Cross-type kits are saveable behind an explicit acknowledgment;
        // no-device SKUs are never mappable, so they get only the plain error.
        if (errorBody?.standard_error?.code === 'smartmeter_sku_cross_type' && !allowModelMismatch) {
          if (window.confirm(`${errorBody.standard_error.message}\n\n${Strings.shipping.smartmeterSaveAnyway}`)) {
            submit(device, true);
          }
          return;
        }
        showNotification(errorBody?.error?.message || Strings.shipping.smartmeterConfigureSkuFailed, 5000, true);
        return;
      }
      load();
      setShowSmartmeterConfigModal(false);
      setSelectedDevice(null);
    });
  }, [configureSmartmeterSku, showNotification, load, smartmeterSkus]);

  const manufacturers = React.useMemo(() => _.uniqBy(data, 'device_manufacturer').map(d => d.device_manufacturer).filter(m => !!m), [data]);

  const modelTypes = React.useMemo(() => modelTypesFromDevices(data), [data]);

  return (
    <>
      <PageHeader
        left={Strings.DeviceCatalog}
        right={(
          <>
            <TextInput
              placeholder={Strings.search}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <AdvancedFilters>
              <Filter definition={getFiltersDefinition({ includeHub: true }).DeviceType} />
              <Filter definition={ManufactuterFilter(manufacturers).manufacturer} />
            </AdvancedFilters>
            {isDevelopment() && (
              <Button onClick={() => {smartmeterBootstrap(); load();}} title="Bootstrap Smartmeter SKUs" class={HEADER_BUTTON_DARK_BLUE} />
            )}
          </>
        )}
      />
      <AdvancedFiltersBar customFilters={ManufactuterFilter(manufacturers)} />
      <Table3
        data={dataForTable}
        columns={columns}
        name="DeviceCatalog"
        buttons={buttons}
        enableColumnFiltering
        pinFirstColumn
        enableRowSelection
        onRowSelectionChange={setSelectedRows}
        ref={tableRef}
      />
      <ActionWidget show={selectedRows?.length > 0}>
        <ActionDropdownOnOffMenu
          img="action-edit"
          tooltiptext={Strings.isOrderable}
          off={{label: Strings.formatString(Strings.actionOptionOff, Strings.isOrderable)}}
          on={{label: Strings.formatString(Strings.actionOptionOn, Strings.isOrderable)}}
          action={value => onIsOrderableAction(selectedRows, value)}
        />
      </ActionWidget>
      {devicesToDisable && (
        <SimpleModal
          name="disable-device-modal"
          title={Strings.disableDeviceTitle}
          onCancel={() => setDevicesToDisable(null)}
          onSubmit={() => { setDevicesOrderable(devicesToDisable, false); setDevicesToDisable(null); }}
          content={<div>{Strings.disableDeviceMappingsWarning}</div>}
        />
      )}
      {showSmartmeterConfigModal && selectedDevice && (
        <SmartmeterConfigModal
          device={selectedDevice}
          onCancel={() => { setShowSmartmeterConfigModal(false); setSelectedDevice(null); }}
          onSubmit={onConfigureSmartmeterSku}
          smartmeterSkus={smartmeterSkus}
          modelTypes={modelTypes}
        />
      )}
    </>
  );
}

DeviceCatalog.propTypes = {
  getCatalog: PropTypes.func.isRequired,
  setGlobalOrderable: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
  showNotification: PropTypes.func.isRequired,
  getSmartmeterSkus: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  return {
    filters: state.entities.advancedFilters.filters.items,
  };
};

const mapDispatchToProps = {
  getCatalog: shippingActions.getCatalog,
  setGlobalOrderable: shippingActions.setGlobalOrderable,
  getSmartmeterSkus: shippingActions.getSmartmeterSkus,
  smartmeterBootstrap: shippingActions.smartmeterBootstrap,
  configureSmartmeterSku: shippingActions.configureSmartmeterSku,
  showNotification: notificationActions.show,
};

export default connect(mapStateToProps, mapDispatchToProps)(DeviceCatalog);
